/**
 * `ReactionStakes` — a damage reaction's body: two outcome tiles and the one-line cost
 * summary (`comms-attack-reaction-cards` 4.1/4.2). The engine rolls damage before it raises the
 * prompt, so the decider sees both outcomes before choosing:
 *
 *     TAKE IT  8 KIN    |    BRACE  4 KIN
 *     Uses your reaction · next turn: 1 quick action, no reactions
 *
 * **The one place this layout lives.** COMMS's `DecisionCard` and the floating `PromptModal` both
 * render it. The tile colours come from `--rs-*` custom properties with light-paper defaults (the
 * modal); the dark COMMS card overrides them.
 *
 * A rider weapon's multi-instance `breakdown` keeps one tile per outcome: the tile shows the summed
 * figure (the top-level `ifDeclined`/`ifReacted` already are sums) and lists every instance's type
 * (`7 KIN + BURN`).
 */
import type { PromptOption, PromptRequest } from "@lancer-vtt/shared";
import { damageTypeCode } from "./damageTypeCode";
import "./ReactionStakes.css";

type IncomingDamage = NonNullable<PromptRequest["incomingDamage"]>;

/** The option id every reaction registry records a decline under (`DECLINE_OPTION` and its siblings). */
export const DECLINE_OPTION_ID = "decline";

/** The accepting option — the first that isn't the decline. Its label names the reacted tile. */
export function acceptOption(options: readonly PromptOption[]): PromptOption | undefined {
  return options.find((o) => o.id !== DECLINE_OPTION_ID);
}

/**
 * Whether a prompt/decision renders as the compact question + tiles body instead of its authored
 * sentence. The reacted tile shows `incomingDamage.ifReacted`, which the engine computes for the
 * TARGET with Resistance applied: true for Brace, false for a redirect (Intercede moves the hit
 * onto the protector, whose sentence names whose damage it is) or an authored offer whose effect
 * isn't Resistance. So the gate is the engine-authored `summary`, which only Brace sets; every
 * other damage offer keeps its sentence, which says what accepting does.
 */
export function hasReactionStakes(p: {
  title?: string | undefined;
  summary?: string | undefined;
  incomingDamage?: PromptRequest["incomingDamage"];
}): p is { title: string; summary: string; incomingDamage: IncomingDamage } {
  return p.incomingDamage !== undefined && p.title !== undefined && p.summary !== undefined;
}

/**
 * The question headline over the tiles: `Brace against Ronin's hit?`, or `Brace?` when the source
 * has no name this client can show.
 */
export function reactionQuestion(title: string, sourceName: string | undefined): string {
  return sourceName !== undefined ? `${title} against ${sourceName}'s hit?` : `${title}?`;
}

/** The type codes a tile lists: the one type, or every instance's type in order, deduplicated. */
function typeCodes(dmg: IncomingDamage): string {
  const types = dmg.breakdown !== undefined ? [...new Set(dmg.breakdown.map((b) => b.type))] : [dmg.type];
  return types.map(damageTypeCode).join(" + ");
}

function Tile({ label, amount, types, accept }: { label: string; amount: number; types: string; accept: boolean }) {
  return (
    <div className={accept ? "rs-tile rs-tile-accept" : "rs-tile"}>
      <span className="rs-tile-label">{label}</span>
      <span className="rs-tile-value">
        {amount} {types}
      </span>
    </div>
  );
}

export function ReactionStakes({
  dmg,
  acceptLabel,
  summary,
}: {
  dmg: IncomingDamage;
  /** The accepting option's label (`Brace`) — names the reacted tile. */
  acceptLabel: string;
  /** The engine-authored cost line. Required: `hasReactionStakes` gates on it, so every caller has one. */
  summary: string;
}) {
  const types = typeCodes(dmg);
  return (
    <div className="rs" aria-label="incoming damage">
      <div className="rs-tiles">
        <Tile label="TAKE IT" amount={dmg.ifDeclined} types={types} accept={false} />
        <Tile label={acceptLabel.toUpperCase()} amount={dmg.ifReacted} types={types} accept />
      </div>
      <p className="rs-summary">{summary}</p>
    </div>
  );
}
